import dayjs, { Dayjs } from 'dayjs';

export const PAUSE_CANCELLATION_DEADLINE_DAY = 10;

export type TSubscriptionPauseDuration = '1' | '2' | '3';

export interface ISubscriptionPauseDates {
	pauseStart: string;
	lastDateBeforePause: string;
	activeFrom: string;
}

export interface ISubscriptionEditedPauseDates extends ISubscriptionPauseDates {
	lastSavedPauseEnd: string;
}

export interface ISubscriptionPauseFormValues {
	duration: TSubscriptionPauseDuration | null;
}

export interface IConditionsToHidePauseOptions {
	isThirdMonthOfPause: boolean;
	monthsPassed: number;
}

export interface IPauseDatesForInfoMessage {
	isTodayAfterDeadline: boolean;
	deadlineMonth: string;
	pauseStartMonthBeforeDeadline: string;
	pauseStartMonthAfterDeadline: string;
}

/** Calculate initial pause (no pause has been scheduled before) */
export const calculateSubscriptionPauseDates = (
	durationMonths: number,
	lastDateBeforePause: string
): ISubscriptionPauseDates => {
	const dateBeforePause = dayjs(lastDateBeforePause);
	const pauseStart = dateBeforePause.add(1, 'day');
	const activeFromMonth = pauseStart.add(durationMonths, 'month');

	return {
		pauseStart: pauseStart.format('DD.MM.YYYY'),
		lastDateBeforePause: dateBeforePause.format('DD.MM.YYYY'),
		activeFrom: activeFromMonth.format('DD.MM.YYYY'),
	};
};

/** Calculate new pause dates (pause is scheduled or active) */
export const calculateEditedPauseDates = (
	durationMonths: number,
	pauseStartDate: string,
	pauseEndDate: string
): ISubscriptionEditedPauseDates => {
	const pauseStart = dayjs(pauseStartDate);
	const lastSavedPauseEnd = dayjs(pauseEndDate);

	const lastDateBeforePause = pauseStart.subtract(1, 'day');
	const activeFromMonth = pauseStart.add(durationMonths, 'month');

	return {
		pauseStart: pauseStart.format('DD.MM.YYYY'),
		lastSavedPauseEnd: lastSavedPauseEnd.format('DD.MM.YYYY'),
		lastDateBeforePause: lastDateBeforePause.format('DD.MM.YYYY'),
		activeFrom: activeFromMonth.format('DD.MM.YYYY'),
	};
};

export const getPauseDurationInMonths = (
	pauseStart: string | null,
	pauseEnd: string | null
): TSubscriptionPauseDuration => {
	const start = dayjs(pauseStart).startOf('month');
	const end = dayjs(pauseEnd).startOf('month');

	return String(end.diff(start, 'month') + 1) as TSubscriptionPauseDuration;
};

export const isAfterTenthOfMonth = (monthOfStart: string | Dayjs): boolean => {
	const currentDay = dayjs().date();
	const isTodayAfterTenth = currentDay > PAUSE_CANCELLATION_DEADLINE_DAY;
	const selectedMonth = dayjs.isDayjs(monthOfStart)
		? monthOfStart
		: dayjs(monthOfStart);
	const isCurrentSelected = selectedMonth.isValid()
		? selectedMonth.isSame(dayjs(), 'month')
		: false;

	return isTodayAfterTenth && isCurrentSelected;
};

export const isPauseStartInvalidAfterTenth = (pauseStart: string): boolean => {
	const today = dayjs();
	const isAfterTenth = today.date() > PAUSE_CANCELLATION_DEADLINE_DAY;
	const pauseStartMonth = dayjs(pauseStart);
	const isNextMonth = pauseStartMonth.isSame(today.add(1, 'month'), 'month');
	return isAfterTenth && isNextMonth;
};

export const getPauseDatesForInfoMessage = (): IPauseDatesForInfoMessage => {
	const today = dayjs();

	const isTodayAfterDeadline = isAfterTenthOfMonth(today);
	const deadlineMonth = today.format('MMMM');
	const pauseStartMonthBeforeDeadline = today.add(1, 'month').format('MMMM');
	const pauseStartMonthAfterDeadline = today.add(2, 'month').format('MMMM');

	return {
		isTodayAfterDeadline,
		deadlineMonth,
		pauseStartMonthBeforeDeadline,
		pauseStartMonthAfterDeadline,
	};
};

export const getConditionsToHidePauseOptions = (
	pauseStart: string | null
): IConditionsToHidePauseOptions => {
	const currentMonth = dayjs().startOf('month');
	const pauseStartMonth = dayjs(pauseStart).startOf('month');

	const monthsPassed = currentMonth.diff(pauseStartMonth, 'month');
	const isThirdMonthOfPause = monthsPassed + 1 === 3;

	return {
		isThirdMonthOfPause,
		monthsPassed,
	};
};
